import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useGrid } from '../../hooks/useGrid';
import { useAuth } from '../../context/AuthContext';
import { useTasks } from '../../hooks/useTasks';
import type { GridRow } from '@spirtech/shared';
import type { PendingSlot } from '../../store/gridStore';
import type { TaskOption } from '../../hooks/useTasks';
import s from './TimeGrid.module.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const CELL_W = 9;
const ROW_H = 22;
const HPD = 8;
const DAYS_1M = 22; // ~1 month of workdays
const DAYS_3M = 65; // ~3 months of workdays
const DAYS_6M = 130; // ~6 months of workdays
const DAYS_FWD = 10; // 2 workweeks ahead: tomorrow is the initial right edge, +1 week accessible via scroll
const H_WEEK = 22;
const H_DATE = 20;
const H_HOUR = 14;
const HEADER_H = H_WEEK + H_DATE + H_HOUR;

const SEP_HOUR = 'rgba(180,180,180,0.35)';
const SEP_DAY = '#000';
const SEP_WEEKEND = '#000';
const GREEN = '#00BD00';
const RED = '#BD0000';
const BLU_HOV = '#93c5fd';
const BG_TODAY = 'rgba(59,130,246,0.07)';
const YEL = '#fef08a';
const YEL_TXT = '#78350f';
const HOUR_GREEN = '#6dc96d'; // lighter green for hour label row
const HOUR_RED = '#de6060'; // lighter red for hour label row
const HOV_GREEN_Y = '#aaee44'; // green-yellow: hovered filled hour
const HOV_RED_Y = '#ffb347'; // red-yellow: hovered conflict hour

const TEAM_PALETTE = [
	{ bg: '#E1F5EE', color: '#0F6E56' },
	{ bg: '#E6F1FB', color: '#185FA5' },
	{ bg: '#EEEDFE', color: '#534AB7' },
	{ bg: '#FAEEDA', color: '#854F0B' },
	{ bg: '#FBEAF0', color: '#993556' },
	{ bg: '#FAECE7', color: '#993C1D' },
	{ bg: '#FEF9C3', color: '#854D0E' },
	{ bg: '#F0FDF4', color: '#166534' },
	{ bg: '#FDF4FF', color: '#7E22CE' },
	{ bg: '#FFF7ED', color: '#C2410C' },
];

// ── Date helpers ───────────────────────────────────────────────────────────────
function addWorkdays(base: Date, delta: number): Date {
	const dt = new Date(base);
	let rem = Math.abs(delta);
	const step = delta > 0 ? 1 : -1;
	while (rem > 0) {
		dt.setDate(dt.getDate() + step);
		const dow = dt.getDay();
		if (dow !== 0 && dow !== 6) rem--;
	}
	return dt;
}

function buildWeekdays(today: Date, daysBack: number): Date[] {
	const days: Date[] = [];
	const before: Date[] = [];
	let cursor = new Date(today);
	for (let i = 0; i < daysBack; i++) {
		cursor = addWorkdays(cursor, -1);
		before.push(new Date(cursor));
	}
	before.reverse().forEach((d) => days.push(d));
	days.push(new Date(today));
	cursor = new Date(today);
	for (let i = 0; i < DAYS_FWD; i++) {
		cursor = addWorkdays(cursor, 1);
		days.push(new Date(cursor));
	}
	return days;
}

function isLastDayOfWeek(weekdays: Date[], di: number): boolean {
	if (di >= weekdays.length - 1) return false;
	return (weekdays[di + 1].getTime() - weekdays[di].getTime()) / 86400000 > 1;
}

/*
   Format a Date as YYYY-MM-DD using LOCAL date components.
   toISOString() converts to UTC and shifts the date by one day for users
   east of UTC when the Date is at local midnight (e.g. CEST midnight is
   22:00 UTC of the previous day), which previously made grid columns
   look up the previous day's slots.
*/
function isoDate(dt: Date): string {
	const vY = dt.getFullYear(); // local year
	const vM = String(dt.getMonth() + 1).padStart(2, '0'); // local month, 1-based
	const vD = String(dt.getDate()).padStart(2, '0'); // local day-of-month
	return `${vY}-${vM}-${vD}`;
}

function weekNum(dt: Date): number {
	const d = new Date(dt);
	d.setHours(0, 0, 0, 0);
	d.setDate(d.getDate() + 4 - (d.getDay() || 7));
	const y = new Date(d.getFullYear(), 0, 1);
	return Math.ceil(((d.getTime() - y.getTime()) / 86400000 + 1) / 7);
}

type SortKey = 'teamName' | 'projectName' | 'userInitials' | 'taskName';

// ── Component ──────────────────────────────────────────────────────────────────
interface Props {
	currentUserId: number;
}

export function TimeGrid({ currentUserId }: Props) {
	const { user } = useAuth();
	const isManager = user?.isManager ?? false;

	const {
		rows,
		loading,
		error,
		actionError,
		clearActionError,
		canUndo,
		canRedo,
		isFilled,
		isConflict,
		commit,
		undo,
		redo,
		reload,
	} = useGrid(currentUserId);
	const { createTask, fetchTasks, fetchProjects, moveTask } = useTasks();

	// ── Dynamic team colour map (sorted team names → palette entries) ──────────
	const teamColorMap = useMemo(() => {
		const names = [...new Set(rows.map((r) => r.teamName))].sort();
		const map = new Map<string, { bg: string; color: string }>();
		names.forEach((n, i) => map.set(n, TEAM_PALETTE[i % TEAM_PALETTE.length]));
		return map;
	}, [rows]);

	// ── Add-task panel state ───────────────────────────────────────────────────
	const [allProjects, setAllProjects] = useState<{ id: number; name: string; client: string | null }[]>([]);
	// Tracks whether the /tasks/my-projects fetch has completed (resolved or failed)
	// so we can tell the difference between "still loading projects" and "user truly
	// has none assigned" when picking which empty-state message to show.
	const [projectsLoaded, setProjectsLoaded] = useState(false);

	useEffect(() => {
		fetchProjects()
			.then((ps) => {
				setAllProjects(
					[...ps].sort((a, b) => {
						const va = a.client ? `${a.client} - ${a.name}` : a.name;
						const vb = b.client ? `${b.client} - ${b.name}` : b.name;
						return va.localeCompare(vb, 'fr');
					}),
				);
			})
			.catch(() => {})
			.finally(() => setProjectsLoaded(true));
	}, []);

	// "New task" form starts empty — even when the user is a member of a single
	// project. Auto-selecting it would have the side effect of putting the
	// project on screen as if the user was about to add a task, which they
	// usually aren't on a fresh page load.
	const [atProjectId, setAtProjectId] = useState('');
	const [atTasks, setAtTasks] = useState<TaskOption[]>([]);
	const [atName, setAtName] = useState('');
	const [atError, setAtError] = useState<string | null>(null);
	const [atSaving, setAtSaving] = useState(false);

	// ── Move-task menu state ───────────────────────────────────────────────────
	// When set, a floating project picker is anchored at viewport coords (x, y)
	// for the row being moved. Rendered fixed-position so the left panel's
	// overflow clipping never hides it.
	const [moveMenu, setMoveMenu] = useState<{ row: GridRow; x: number; y: number } | null>(null);
	const [moveFilter, setMoveFilter] = useState(''); // project search text in the picker
	const [moveError, setMoveError] = useState<string | null>(null); // last move failure message
	const [moving, setMoving] = useState(false); // a move request is in flight

	// Open the picker just below the clicked button; stopPropagation so the row's
	// own handlers (filter toggles, hover) don't also fire.
	function openMoveMenu(inEvent: React.MouseEvent, inRow: GridRow) {
		inEvent.stopPropagation();
		const vRect = (inEvent.currentTarget as HTMLElement).getBoundingClientRect();
		setMoveFilter('');
		setMoveError(null);
		setMoveMenu({ row: inRow, x: vRect.left, y: vRect.bottom + 2 });
	}

	async function handleMove(inProjectId: number) {
		if (!moveMenu || moving) return;
		setMoving(true);
		setMoveError(null);
		try {
			await moveTask(moveMenu.row.taskId, inProjectId);
			setMoveMenu(null);
			await reload(isoDate(addWorkdays(today, -daysBack)), isoDate(addWorkdays(today, DAYS_FWD)), showAllTasks);
		} catch (err: any) {
			setMoveError(err.message);
		} finally {
			setMoving(false);
		}
	}

	async function handleAtProjectChange(pid: string) {
		setAtProjectId(pid);
		setAtName('');
		setAtTasks([]);
		if (!pid) return;
		try {
			setAtTasks(await fetchTasks(Number(pid)));
		} catch {
			/* suggestions are optional */
		}
	}

	async function handleAddTask(e: React.FormEvent) {
		e.preventDefault();
		if (!atProjectId || !atName.trim()) return;
		setAtSaving(true);
		setAtError(null);
		try {
			await createTask({ projectId: Number(atProjectId), name: atName.trim() });
			setAtName('');
			setAtProjectId('');
			setAtTasks([]);
			await reload(isoDate(addWorkdays(today, -daysBack)), isoDate(addWorkdays(today, DAYS_FWD)), showAllTasks);
		} catch (err: any) {
			setAtError(err.message);
		} finally {
			setAtSaving(false);
		}
	}

	const today = useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		return d;
	}, []);
	const [daysBack, setDaysBack] = useState(DAYS_1M);
	const weekdays = useMemo(() => buildWeekdays(today, daysBack), [today, daysBack]);
	// Always-current ref used inside effects that can't list weekdays as a dep
	const weekdaysRef = useRef(weekdays);
	weekdaysRef.current = weekdays;
	const todayIdx = useMemo(() => weekdays.findIndex((d) => d.getTime() === today.getTime()), [weekdays, today]);

	// ── Sort / filter ──────────────────────────────────────────────────────────
	const [sortKey, setSortKey] = useState<SortKey | null>('projectName');
	const [sortDir, setSortDir] = useState<1 | -1>(1);
	const [filterPerson, setFilterPerson] = useState<string | null>(() => user?.initials ?? null);
	const [filterProject, setFilterProject] = useState<string | null>(null);
	const [showAllTasks, setShowAllTasks] = useState(false);

	const displayRows = useMemo(() => {
		const r = [...rows];
		if (sortKey)
			r.sort((a, b) => {
				let va: string | number, vb: string | number;
				if (sortKey === 'projectName') {
					va = a.projectClient ? `${a.projectClient} - ${a.projectName}` : a.projectName;
					vb = b.projectClient ? `${b.projectClient} - ${b.projectName}` : b.projectName;
				} else {
					va = a[sortKey];
					vb = b[sortKey];
				}
				return va < vb ? -sortDir : va > vb ? sortDir : 0;
			});
		return r.filter((row) => {
			if (filterPerson && row.userInitials !== filterPerson) return false;
			if (filterProject && row.projectName !== filterProject) return false;
			return true;
		});
	}, [rows, sortKey, sortDir, filterPerson, filterProject]);

	// ── Preview state: cells toggled in current drag gesture ──────────────────
	// key: "userId:taskId:date:hour" → true (filled) | false (empty)
	const [preview, setPreview] = useState<Map<string, boolean>>(new Map());

	// Merged isFilled: preview takes priority over store
	const isFilledMerged = useCallback(
		(uid: number, tid: number, date: string, hour: number) => {
			const key = `${uid}:${tid}:${date}:${hour}`;
			if (preview.has(key)) return preview.get(key)!;
			return isFilled(uid, tid, date, hour);
		},
		[isFilled, preview],
	);

	// ── Drag ──────────────────────────────────────────────────────────────────
	const dragging = useRef(false);
	const dragRowKey = useRef('');
	const dragVal = useRef(true);
	const gesture = useRef<PendingSlot[]>([]);
	const dragLastPos = useRef<{ di: number; h: number } | null>(null);
	const dragRowY = useRef<number | null>(null);

	// ── Hover — date label and left row are imperative; the hour label is
	// driven by React via hoveredKey so its colour stays in sync with the
	// filled state (mixing imperative DOM mutation with React inline styles
	// previously left the hour row stuck on the old colour after a drag,
	// because React's VDOM diff saw no change and skipped the DOM update).
	const prevDateLabel = useRef<HTMLElement | null>(null);
	const prevLeftRow = useRef<HTMLElement | null>(null);
	// Track hovered cell key — drives blue highlight on the cell AND
	// the yellow tint on the corresponding hour label
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);

	const gridScrollRef = useRef<HTMLDivElement>(null);
	const leftBodyRef = useRef<HTMLDivElement>(null);
	const leftHeaderRef = useRef<HTMLDivElement>(null);

	// ── Virtual scroll ────────────────────────────────────────────────────────
	const OVERSCAN = 8;
	const [vStart, setVStart] = useState(0);
	const vStartRef = useRef(0);
	const [containerH, setContainerH] = useState(600);

	// ── Clear hover header/left highlights ────────────────────────────────────
	const clearHeaderHover = useCallback(() => {
		if (prevDateLabel.current) {
			const pdi = Number(prevDateLabel.current.dataset.di);
			const isT = weekdays[pdi]?.getTime() === today.getTime();
			prevDateLabel.current.style.background = '';
			prevDateLabel.current.style.color = isT ? '#1d4ed8' : '#000';
			prevDateLabel.current.style.fontWeight = isT ? '500' : '';
		}
		if (prevLeftRow.current) prevLeftRow.current.style.background = '';
		prevDateLabel.current = null;
		prevLeftRow.current = null;
	}, [weekdays, today]);

	const clearHover = useCallback(() => {
		clearHeaderHover();
		setHoveredKey(null);
	}, [clearHeaderHover]);

	const setHover = useCallback(
		(di: number, h: number, userId: number, taskId: number) => {
			clearHeaderHover();
			const dl = gridScrollRef.current?.querySelector<HTMLElement>(`[data-date-di="${di}"]`) ?? null;
			const lr = leftBodyRef.current?.querySelector<HTMLElement>(`[data-rowkey="${userId}:${taskId}"]`) ?? null;
			if (dl) {
				dl.style.background = YEL;
				dl.style.color = YEL_TXT;
				dl.style.fontWeight = '500';
			}
			if (lr) lr.style.background = 'rgba(250,204,21,0.18)';
			prevDateLabel.current = dl;
			prevLeftRow.current = lr;
			setHoveredKey(`${userId}:${taskId}:${isoDate(weekdays[di])}:${h}`);
		},
		[clearHeaderHover, weekdays],
	);

	// ── Scroll sync + virtual-row tracking ───────────────────────────────────
	// Depends on `loading`: on mount the grid DOM doesn't exist yet (loading=true
	// shows a placeholder), so refs are null. Re-running when loading turns false
	// is the first moment the real grid is in the DOM and refs are populated.
	useEffect(() => {
		const gs = gridScrollRef.current,
			lb = leftBodyRef.current;
		if (!gs || !lb) return;
		const fn = () => {
			lb.scrollTop = gs.scrollTop;
			const next = Math.max(0, Math.floor(gs.scrollTop / ROW_H) - OVERSCAN);
			if (next !== vStartRef.current) {
				vStartRef.current = next;
				setVStart(next);
			}
		};
		gs.addEventListener('scroll', fn, { passive: true });
		// Wheel over the left panel must drive gridScroll, not leftBody directly.
		// The existing sync listener then mirrors leftBody ← gridScroll.
		const onLeftWheel = (e: WheelEvent) => {
			e.preventDefault();
			gs.scrollTop += e.deltaY;
		};
		lb.addEventListener('wheel', onLeftWheel, { passive: false });
		return () => {
			gs.removeEventListener('scroll', fn);
			lb.removeEventListener('wheel', onLeftWheel);
		};
	}, [loading]);

	// ── Container height tracking (for virtual-row count) ─────────────────────
	useEffect(() => {
		const gs = gridScrollRef.current;
		if (!gs) return;
		const obs = new ResizeObserver(() => setContainerH(gs.clientHeight));
		obs.observe(gs);
		setContainerH(gs.clientHeight);
		return () => obs.disconnect();
	}, [loading]);

	// ── Initial scroll: tomorrow is the rightmost visible day ────────────────
	// Days beyond tomorrow (one extra week) remain accessible via horizontal scroll.
	useEffect(() => {
		if (loading || rows.length === 0) return;
		setTimeout(() => {
			const gs = gridScrollRef.current;
			if (!gs) return;
			const vTomorrowIdx = todayIdx + 1; // next workday is always present (DAYS_FWD ≥ 1)
			gs.scrollLeft = Math.max(0, (vTomorrowIdx + 1) * HPD * CELL_W - gs.clientWidth);
		}, 60);
	}, [loading, rows.length, todayIdx]);

	// ── Global mouseup → commit gesture ──────────────────────────────────────
	useEffect(() => {
		function onUp() {
			if (!dragging.current) return;
			dragging.current = false;
			dragRowKey.current = '';
			if (gesture.current.length) {
				const g = gesture.current;
				gesture.current = [];
				commit(g).then(() => {
					// Store is updated — clear preview, React will re-render from store
					setPreview(new Map());
				});
			}
		}
		document.addEventListener('mouseup', onUp);
		return () => document.removeEventListener('mouseup', onUp);
	}, [commit]);

	// ── Global mousemove: keep selection on the drag row even when cursor
	//    moves above or below it. Uses elementFromPoint at the row's locked Y.
	useEffect(() => {
		function onMove(e: MouseEvent) {
			if (!dragging.current || dragRowY.current === null) return;
			const el = document.elementFromPoint(e.clientX, dragRowY.current);
			if (!el) return;
			const cell = (el as Element).closest('.sg-cell') as HTMLElement | null;
			if (!cell) return;
			const userId = Number(cell.dataset.userId);
			const taskId = Number(cell.dataset.taskId);
			if (dragRowKey.current !== `${userId}:${taskId}`) return;
			const di = Number(cell.dataset.di);
			const h = Number(cell.dataset.h);
			const last = dragLastPos.current;
			if (last === null || (last.di === di && last.h === h)) return;
			const from = last.di * HPD + last.h;
			const to = di * HPD + h;
			const step = from <= to ? 1 : -1;
			for (let pos = from + step; pos !== to + step; pos += step) {
				const pdi = Math.floor(pos / HPD);
				const ph = pos % HPD;
				const date = isoDate(weekdaysRef.current[pdi]);
				const key = `${userId}:${taskId}:${date}:${ph}`;
				if (!gesture.current.some((p) => p.taskId === taskId && p.entryDate === date && p.hourSlot === ph)) {
					gesture.current.push({ userId, taskId, entryDate: date, hourSlot: ph, filled: dragVal.current });
					setPreview((prev) => {
						const next = new Map(prev);
						next.set(key, dragVal.current);
						return next;
					});
				}
			}
			dragLastPos.current = { di, h };
		}
		document.addEventListener('mousemove', onMove);
		return () => document.removeEventListener('mousemove', onMove);
	}, []);

	// ── Keyboard shortcuts ────────────────────────────────────────────────────
	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if (!e.ctrlKey && !e.metaKey) return;
			if (e.key === 'z' || e.key === 'Z') {
				e.preventDefault();
				undo();
			}
			if (e.key === 'y' || e.key === 'Y') {
				e.preventDefault();
				redo();
			}
		}
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [undo, redo]);

	// ── Cell interaction ──────────────────────────────────────────────────────
	function onCellDown(e: React.MouseEvent<HTMLElement>) {
		e.preventDefault();
		const cell = e.currentTarget;
		const userId = Number(cell.dataset.userId);
		const taskId = Number(cell.dataset.taskId);
		const di = Number(cell.dataset.di);
		const h = Number(cell.dataset.h);
		const date = isoDate(weekdays[di]);
		dragging.current = true;
		dragRowKey.current = `${userId}:${taskId}`;
		dragVal.current = !isFilledMerged(userId, taskId, date, h);
		gesture.current = [];
		dragLastPos.current = { di, h };
		dragRowY.current = cell.getBoundingClientRect().top + ROW_H / 2;
		toggleCell(userId, taskId, di, h);
	}

	function onCellEnter(e: React.MouseEvent<HTMLElement>) {
		const cell = e.currentTarget;
		const userId = Number(cell.dataset.userId);
		const taskId = Number(cell.dataset.taskId);
		const di = Number(cell.dataset.di);
		const h = Number(cell.dataset.h);
		if (dragging.current && dragRowKey.current === `${userId}:${taskId}`) {
			const last = dragLastPos.current;
			if (last !== null) {
				// Fill all cells between the last detected position and the current one,
				// so that cells skipped by fast mouse movement are not missed.
				const from = last.di * HPD + last.h;
				const to = di * HPD + h;
				const step = from <= to ? 1 : -1;
				for (let pos = from + step; pos !== to + step; pos += step) {
					toggleCell(userId, taskId, Math.floor(pos / HPD), pos % HPD);
				}
			}
			dragLastPos.current = { di, h };
		}
		setHover(di, h, userId, taskId);
	}

	function toggleCell(userId: number, taskId: number, di: number, h: number) {
		const date = isoDate(weekdays[di]);
		const key = `${userId}:${taskId}:${date}:${h}`;
		if (gesture.current.some((p) => p.taskId === taskId && p.entryDate === date && p.hourSlot === h)) return;
		gesture.current.push({ userId, taskId, entryDate: date, hourSlot: h, filled: dragVal.current });
		// Update preview immediately — triggers React re-render with correct colour
		setPreview((prev) => {
			const next = new Map(prev);
			next.set(key, dragVal.current);
			return next;
		});
	}

	// ── Period selector ───────────────────────────────────────────────────────
	function handlePeriod(days: number) {
		setDaysBack(days);
		setVStart(0);
		vStartRef.current = 0;
		reload(isoDate(addWorkdays(today, -days)), isoDate(addWorkdays(today, DAYS_FWD)), showAllTasks);
	}

	function handleToggleShowAll() {
		const next = !showAllTasks;
		setShowAllTasks(next);
		reload(isoDate(addWorkdays(today, -daysBack)), isoDate(addWorkdays(today, DAYS_FWD)), next);
	}

	// ── Click guard ─────────────────────────────────────────────────────────
	// A double-click on a project / person label fires onClick twice, which
	// would toggle the filter on then straight back off. The first click also
	// reorders/filters the rows, so the second click may land on a different
	// label — keying the guard per target therefore misses it. Instead ignore
	// ANY click that arrives within 300 ms of the previous one, so a double-
	// click behaves like a single click.
	const lastClickRef = useRef(0); // timestamp of the last accepted click, ms
	function guardClick(inFn: () => void) {
		return () => {
			const vNow = Date.now(); // current time, ms
			if (vNow - lastClickRef.current < 300) return;
			lastClickRef.current = vNow;
			inFn();
		};
	}

	// ── Sort ──────────────────────────────────────────────────────────────────
	function handleSort(key: SortKey) {
		if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
		else {
			setSortKey(key);
			setSortDir(1);
		}
	}

	// ── Week groups ───────────────────────────────────────────────────────────
	const weekGroups = useMemo(() => {
		const groups: { wn: number; label: string; count: number }[] = [];
		let cur: (typeof groups)[0] | null = null;
		weekdays.forEach((dt) => {
			const wn = weekNum(dt);
			if (!cur || cur.wn !== wn) {
				cur = {
					wn,
					label: `S${wn} – ${dt.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`,
					count: 0,
				};
				groups.push(cur);
			}
			cur.count++;
		});
		return groups;
	}, [weekdays]);

	// ── Cell background — pure function, no side effects ─────────────────────
	function cellBg(userId: number, taskId: number, date: string, h: number, isT: boolean): string {
		const key = `${userId}:${taskId}:${date}:${h}`;
		const hov = hoveredKey === key;
		const fil = isFilledMerged(userId, taskId, date, h);
		if (fil) return isConflict(userId, taskId, date, h) ? RED : GREEN;
		if (hov) return BLU_HOV;
		if (isT) return BG_TODAY;
		return '';
	}

	function hourLabelBg(di: number, h: number): string {
		const date = isoDate(weekdays[di]);
		let hasRed = false,
			hasGreen = false;
		for (const row of displayRows) {
			if (row.userId !== currentUserId) continue;
			if (isFilledMerged(row.userId, row.taskId, date, h)) {
				if (isConflict(row.userId, row.taskId, date, h)) hasRed = true;
				else hasGreen = true;
			}
		}
		// Apply hover tint when this (date, h) matches the cell currently hovered.
		// hoveredKey format: `${userId}:${taskId}:${date}:${h}` → 4 segments.
		if (hoveredKey) {
			const vParts = hoveredKey.split(':');
			if (vParts[2] === date && Number(vParts[3]) === h) {
				if (hasRed) return HOV_RED_Y;
				if (hasGreen) return HOV_GREEN_Y;
				return YEL;
			}
		}
		if (hasRed) return HOUR_RED;
		if (hasGreen) return HOUR_GREEN;
		return '#fff';
	}

	// ── Early returns ─────────────────────────────────────────────────────────
	// Note: we deliberately do NOT early-return when rows is empty. The user may
	// be a member of projects but have no time entries / pinned tasks yet, in
	// which case they need the add-task form to create their first task. The
	// empty-state message is rendered inline below, where the grid would go.
	if (error) return <div className={s.msg}>Erreur : {error}</div>;
	if (loading) return <div className={s.msg}>Chargement…</div>;

	// ── Virtual rows ──────────────────────────────────────────────────────────
	const visibleCount = Math.ceil(containerH / ROW_H) + OVERSCAN * 2;
	const vEnd = Math.min(displayRows.length, vStart + visibleCount);
	const virtualRows = displayRows.slice(vStart, vEnd);
	const padTop = vStart * ROW_H;
	const padBottom = Math.max(0, (displayRows.length - vEnd) * ROW_H);

	const HEADERS: [SortKey, string][] = [
		['teamName', 'Pôle'],
		['projectName', 'Projet'],
		['userInitials', 'Qui'],
		['taskName', 'Tâche'],
	];

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<div className={s.app} onMouseLeave={clearHover}>
			{/*
         Transient action error — non-blocking. A failed commit/undo/redo
         drops a localised message into store.actionError; we render it as a
         bar at the top until the next successful action clears it (or the
         user dismisses it manually). Distinct from `error` (full-screen
         load failure) — see store/gridStore.ts.
      */}
			{actionError && (
				<div
					role="alert"
					style={{
						background: '#fef2f2',
						color: '#7f1d1d',
						border: '1px solid #fca5a5',
						borderRadius: 4,
						padding: '8px 12px',
						margin: '6px 8px',
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						fontSize: 14,
					}}
				>
					<span style={{ flex: 1 }}>{actionError}</span>
					<button
						type="button"
						onClick={clearActionError}
						style={{
							background: 'transparent',
							border: 'none',
							color: '#7f1d1d',
							cursor: 'pointer',
							fontWeight: 600,
							fontSize: 13,
						}}
					>
						Fermer ×
					</button>
				</div>
			)}

			{/* Filter bar */}
			<div className={s.filterBar}>
				<span>Affichage :</span>
				{!filterPerson && !filterProject && (
					<span className={`${s.chip} ${s.chipActive}`}>Toutes les saisies</span>
				)}
				{filterPerson && (
					<span className={`${s.chip} ${s.chipActive}`} onClick={() => setFilterPerson(null)}>
						Personne : {filterPerson} ×
					</span>
				)}
				{filterProject && (
					<span className={`${s.chip} ${s.chipActive}`} onClick={() => setFilterProject(null)}>
						Projet : {filterProject} ×
					</span>
				)}
				<span className={s.filterHint}>clic sur initiales ou projet · clic entête pour trier</span>
				{isManager && user?.initials && (
					<button
						className={`${s.undoBtn} ${filterPerson === user.initials ? s.undoBtnActive : ''}`}
						onClick={() => setFilterPerson((f) => (f === user!.initials ? null : user!.initials))}
					>
						{user.initials}
					</button>
				)}
				<button className={`${s.undoBtn} ${showAllTasks ? s.undoBtnActive : ''}`} onClick={handleToggleShowAll}>
					Toutes les tâches
				</button>
				<button
					className={`${s.undoBtn} ${daysBack === DAYS_1M ? s.undoBtnActive : ''}`}
					onClick={() => handlePeriod(DAYS_1M)}
				>
					1 mois
				</button>
				<button
					className={`${s.undoBtn} ${daysBack === DAYS_3M ? s.undoBtnActive : ''}`}
					onClick={() => handlePeriod(DAYS_3M)}
				>
					3 mois
				</button>
				<button
					className={`${s.undoBtn} ${daysBack === DAYS_6M ? s.undoBtnActive : ''}`}
					onClick={() => handlePeriod(DAYS_6M)}
				>
					6 mois
				</button>
				<button className={s.undoBtn} onClick={undo} disabled={!canUndo}>
					↩ Annuler
				</button>
				<button className={s.undoBtn} onClick={redo} disabled={!canRedo}>
					↪ Rétablir
				</button>
			</div>

			<form className={s.addTaskBar} onSubmit={handleAddTask}>
				<span className={s.addTaskLabel}>Nouvelle tâche :</span>
				<select
					required
					className={s.addTaskSelect}
					value={atProjectId}
					onChange={(e) => handleAtProjectChange(e.target.value)}
				>
					<option value="">Projet…</option>
					{allProjects.map((p) => (
						<option key={p.id} value={p.id}>
							{p.client ? `${p.client} - ${p.name}` : p.name}
						</option>
					))}
				</select>
				<div className={s.atInputWrap}>
					<input
						required
						className={s.addTaskInput}
						placeholder={atProjectId ? 'Choisir ou saisir…' : 'Nom de la tâche…'}
						value={atName}
						onChange={(e) => {
							setAtName(e.target.value);
							setAtError(null);
						}}
						disabled={!atProjectId}
						autoComplete="off"
					/>
					{atProjectId && atTasks.length > 0 && (
						<div className={s.atDropdown}>
							{atTasks
								.filter(
									(t) =>
										t.isActive && (!atName || t.name.toLowerCase().includes(atName.toLowerCase())),
								)
								.map((t) => (
									<div
										key={t.id}
										className={`${s.atDropItem} ${atName === t.name ? s.atDropItemActive : ''}`}
										onMouseDown={(e) => {
											e.preventDefault();
											setAtName(t.name);
											setAtError(null);
										}}
									>
										{t.name}
									</div>
								))}
						</div>
					)}
				</div>
				{atError && <span className={s.addTaskError}>{atError}</span>}
				<button type="submit" className={s.undoBtn} disabled={atSaving || !atProjectId || !atName.trim()}>
					{atSaving ? 'Ajout…' : 'Ajouter'}
				</button>
			</form>

			{rows.length === 0 ? (
				<div className={s.msg}>
					{projectsLoaded && allProjects.length === 0
						? 'Aucun projet assigné. Demandez à votre responsable de vous ajouter à un projet.'
						: 'Aucune tâche à afficher. Utilisez le formulaire ci-dessus pour ajouter votre première tâche.'}
				</div>
			) : (
				<div className={s.scrollContainer}>
					{/* ── Fixed left ── */}
					<div className={s.fixedLeft}>
						<div className={s.fixedLeftHeader} ref={leftHeaderRef} style={{ height: HEADER_H }}>
							{/* Empty header cell above the per-row "move to project" buttons */}
							<div className={`${s.leftColHeader} ${s.colMove}`} onMouseEnter={clearHover} />
							{HEADERS.map(([key, label]) => {
								const cls =
									key === 'teamName'
										? s.colTeam
										: key === 'projectName'
											? s.colProj
											: key === 'userInitials'
												? s.colInit
												: s.colTask;
								return (
									<div
										key={key}
										className={`${s.leftColHeader} ${cls}`}
										onMouseEnter={clearHover}
										onClick={() => handleSort(key)}
									>
										{label}
										{sortKey === key ? (sortDir === 1 ? ' ↑' : ' ↓') : ''}
									</div>
								);
							})}
						</div>

						<div className={s.fixedLeftBody} ref={leftBodyRef}>
							<div style={{ paddingTop: padTop, paddingBottom: padBottom }}>
								{virtualRows.map((row) => {
									const rk = `${row.userId}:${row.taskId}`;
									const tc = teamColorMap.get(row.teamName) ?? { bg: '#e5e5e5', color: '#404040' };
									return (
										<div
											key={rk}
											className={s.rowLeft}
											data-rowkey={rk}
											style={{ height: ROW_H, display: 'flex' }}
											onMouseEnter={clearHover}
										>
											<div
												className={`${s.rowLeftCell} ${s.colMove}`}
												style={{ justifyContent: 'center' }}
											>
												<button
													type="button"
													className={s.moveBtn}
													title="Déplacer la tâche vers un autre projet"
													onClick={(e) => openMoveMenu(e, row)}
												>
													⇄
												</button>
											</div>
											<div
												className={`${s.rowLeftCell} ${s.colTeam}`}
												style={{ justifyContent: 'center' }}
											>
												<span
													className={s.teamBadge}
													style={{ background: tc.bg, color: tc.color }}
												>
													{row.teamName}
												</span>
											</div>
											<div className={`${s.rowLeftCell} ${s.colProj}`}>
												<div
													className={`${s.projBtn} ${filterProject === row.projectName ? s.pSelProject : ''}`}
													title={
														row.projectClient
															? `${row.projectClient} - ${row.projectName}`
															: row.projectName
													}
													onClick={guardClick(() =>
														setFilterProject((f) =>
															f === row.projectName ? null : row.projectName,
														),
													)}
												>
													{row.projectClient
														? `${row.projectClient} - ${row.projectName}`
														: row.projectName}
												</div>
											</div>
											<div
												className={`${s.rowLeftCell} ${s.colInit}`}
												style={{ justifyContent: 'center' }}
											>
												<div
													className={`${s.initialsBtn} ${filterPerson === row.userInitials ? s.pSelPerson : ''}`}
													onClick={guardClick(() =>
														setFilterPerson((f) =>
															f === row.userInitials ? null : row.userInitials,
														),
													)}
												>
													{row.userInitials}
												</div>
											</div>
											<div className={`${s.rowLeftCell} ${s.colTask}`} title={row.taskName}>
												{row.taskName}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>

					{/* ── Scrollable grid ── */}
					<div className={s.gridScroll} ref={gridScrollRef}>
						<div className={s.gridInner}>
							{/* Header */}
							<div className={s.gridHeader}>
								<div className={s.weekRow} onMouseEnter={clearHover}>
									{weekGroups.map((wg, gi) => (
										<div
											key={wg.wn}
											className={s.weekLabel}
											style={{
												width: wg.count * HPD * CELL_W,
												minWidth: wg.count * HPD * CELL_W,
												borderRight:
													gi < weekGroups.length - 1 ? `3px solid ${SEP_WEEKEND}` : 'none',
											}}
										>
											{wg.label}
										</div>
									))}
								</div>

								<div className={s.dateHourRow}>
									{weekdays.map((dt, di) => {
										const isT = dt.getTime() === today.getTime();
										const isLast = isLastDayOfWeek(weekdays, di);
										return (
											<div
												key={di}
												className={s.dayBlock}
												style={{
													width: HPD * CELL_W,
													borderRight: isLast
														? `3px solid ${SEP_WEEKEND}`
														: `1px solid ${SEP_DAY}`,
												}}
											>
												<div
													className={`${s.dateLabel} ${isT ? s.isToday : ''}`}
													data-date-di={di}
													onMouseEnter={clearHover}
												>
													{isT ? '● ' : ''}
													{dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
												</div>
												<div className={s.hourLabels}>
													{Array.from({ length: HPD }, (_, h) => (
														<div
															key={h}
															className={s.hourLabel}
															data-hour-di={di}
															data-hour-h={h}
															style={{
																width: CELL_W,
																borderRight:
																	h < HPD - 1 ? `0.5px solid ${SEP_HOUR}` : 'none',
																backgroundColor: hourLabelBg(di, h),
															}}
															onMouseEnter={clearHover}
														>
															{h + 1}
														</div>
													))}
												</div>
											</div>
										);
									})}
								</div>
							</div>

							{/* Grid body */}
							<div className={s.gridBody}>
								<div style={{ paddingTop: padTop, paddingBottom: padBottom }}>
									{virtualRows.map((row) => {
										const rk = `${row.userId}:${row.taskId}`;
										return (
											<div
												key={rk}
												className={s.gridRow}
												style={{ height: ROW_H, display: 'flex' }}
											>
												{weekdays.map((dt, di) => {
													const isLast = isLastDayOfWeek(weekdays, di);
													const date = isoDate(dt);
													const isT = dt.getTime() === today.getTime();
													return (
														<div
															key={di}
															className={s.dayCells}
															style={{
																width: HPD * CELL_W,
																borderRight: isLast
																	? `3px solid ${SEP_WEEKEND}`
																	: `1px solid ${SEP_DAY}`,
															}}
														>
															{Array.from({ length: HPD }, (_, h) => (
																<div
																	key={h}
																	className="sg-cell"
																	data-user-id={row.userId}
																	data-task-id={row.taskId}
																	data-di={di}
																	data-h={h}
																	style={{
																		width: CELL_W,
																		height: '100%',
																		flexShrink: 0,
																		cursor: 'pointer',
																		userSelect: 'none',
																		background: cellBg(
																			row.userId,
																			row.taskId,
																			date,
																			h,
																			isT,
																		),
																		borderRight:
																			h < HPD - 1
																				? `0.5px solid ${SEP_HOUR}`
																				: 'none',
																	}}
																	onMouseDown={onCellDown}
																	onMouseEnter={onCellEnter}
																/>
															))}
														</div>
													);
												})}
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* ── Move-to-project picker ── floating, fixed-position ── */}
			{moveMenu && (
				<>
					<div className={s.moveBackdrop} onMouseDown={() => setMoveMenu(null)} />
					<div className={s.moveMenu} style={{ left: moveMenu.x, top: moveMenu.y }}>
						<div className={s.moveMenuHead}>Déplacer « {moveMenu.row.taskName} » vers&nbsp;:</div>
						<input
							autoFocus
							className={s.moveSearch}
							placeholder="Filtrer un projet…"
							value={moveFilter}
							onChange={(e) => setMoveFilter(e.target.value)}
						/>
						{moveError && <div className={s.moveErr}>{moveError}</div>}
						<div className={s.moveList}>
							{allProjects
								.filter((p) => p.id !== moveMenu.row.projectId)
								.filter((p) => {
									const vLabel = p.client ? `${p.client} - ${p.name}` : p.name;
									return vLabel.toLowerCase().includes(moveFilter.toLowerCase());
								})
								.map((p) => (
									<div key={p.id} className={s.moveItem} onMouseDown={() => handleMove(p.id)}>
										{p.client ? `${p.client} - ${p.name}` : p.name}
									</div>
								))}
							{allProjects.filter((p) => p.id !== moveMenu.row.projectId).length === 0 && (
								<div className={s.moveEmpty}>Aucun autre projet accessible</div>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
}
